import {
  calcSymbolBreakPointPrice,
  calcSymbolBuyValue,
  calcSymbolSellValue,
  DeductionsStoreType,
} from '@core/symbol';
import { PortfolioItem } from '@libs/api-reference';

export type PortfolioItemWithWage = PortfolioItem & {
  wage: DeductionsStoreType;
};

export type PortfolioItemWithPrices = ReturnType<
  typeof calcPortfolioSymbolPrices
>;

/**
 * Calculate the profit/loss (return) value of portfolio
 *
 * @param sumReturnValue
 * @param sumBuyTotalTradeVolume
 * @returns {number}
 */
function calcPortfolioReturnValuePercentage(
  sumReturnValue: number,
  sumBuyTotalTradeVolume: number
): number {
  return (sumReturnValue * 100) / sumBuyTotalTradeVolume || 0;
}

function calculateOpenPositionReturnValuePersentage(
  sumValue: number,
  sumConstPrice: number
): number {
  return (sumValue / sumConstPrice) * 100;
}

function calcPortfolioReturnValue({
  symbolSellValue,
  quantity,
  netAveragePrice,
  netAverageDps,
  buyWage,
}: {
  symbolSellValue: number;
  quantity: number;
  netAveragePrice: number;
  netAverageDps: number;
  buyWage: number;
}) {
  return (
    symbolSellValue - quantity * (netAverageDps + netAveragePrice * buyWage)
  );
}

function calcPortfolioSymbolPrices(symbol: PortfolioItemWithWage) {
  let symbolSellValue = 0;
  let symbolBuyValue = 0;
  let returnValue = 0;
  let returnValuePercentage = 0;
  let breakPoint = 0;

  // Bonus Share
  let bonusShareBuyValue = 0;
  let bonusShareSellValue = 0;
  let returnBonusShareValue = 0;
  let returnBonusSharePercentage = 0;

  const netAveragePrice = parseFloat(symbol.netAveragePrice.toFixed(7));
  const netAverageDps = parseFloat(symbol.netAverageDps.toFixed(7));

  if (symbol.wage) {
    const wage = {
      ...symbol.wage,
      sellValue: parseFloat(symbol.wage.sellValue.toFixed(7)),
      buyValue: parseFloat(symbol.wage.buyValue.toFixed(7)),
    };

    symbolSellValue = calcSymbolSellValue(
      { quantity: symbol.quantity, price: symbol.lastTradePrice },
      wage
    );

    symbolBuyValue = calcSymbolBuyValue(
      { quantity: symbol.quantity, price: netAveragePrice },
      wage
    );

    returnValue = calcPortfolioReturnValue({
      symbolSellValue,
      quantity: symbol.quantity,
      netAveragePrice,
      netAverageDps,
      buyWage: wage.buyValue,
    });

    returnValuePercentage = calcPortfolioReturnValuePercentage(
      returnValue,
      symbolBuyValue
    );

    breakPoint = calcSymbolBreakPointPrice(
      netAveragePrice,
      netAverageDps,
      wage
    );

    if (symbol.bonusShare) {
      bonusShareBuyValue = calcSymbolBuyValue(
        {
          quantity: symbol.bonusShare.quantity,
          price: parseFloat(symbol.bonusShare.netAverage.toFixed(7)),
        },
        wage
      );

      bonusShareSellValue = calcSymbolSellValue(
        {
          quantity: symbol.bonusShare.quantity,
          price: symbol.lastTradePrice,
        },
        wage
      );

      returnBonusShareValue = calcPortfolioReturnValue({
        buyWage: wage.buyValue,
        netAverageDps: symbol.bonusShare.netAverageDPS,
        netAveragePrice: symbol.bonusShare.netAverage,
        quantity: symbol.bonusShare.quantity,
        symbolSellValue: bonusShareSellValue,
      });

      returnBonusSharePercentage = calcPortfolioReturnValuePercentage(
        returnBonusShareValue,
        bonusShareBuyValue
      );
    }
  }

  return {
    ...symbol,
    value: symbolSellValue,
    buyTotalTradeVolume: symbolBuyValue,
    netAveragePrice,
    returnValue,
    returnValuePercentage,
    symbolBuyValue,
    breakPoint,
    bonusShare: symbol?.bonusShare
      ? {
          ...symbol?.bonusShare,
          bonusShareBuyValue,
          bonusShareSellValue,
          returnBonusShareValue,
          returnBonusSharePercentage,
        }
      : null,
  };
}

export {
  calcPortfolioReturnValuePercentage,
  calcPortfolioSymbolPrices,
  calculateOpenPositionReturnValuePersentage,
};
